<template>
  <BaseDialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :width="dialogWidth"
    :draggable="false"
    :dialogClass="windowWidth < 768 ? 'mobile-dialog' : ''"
    @keydown.esc="$emit('update:visible', false)"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-passphrase-title"
  >
    <template #header>
      <!-- Mobile: top row with back + close, title below -->
      <div v-if="windowWidth < 768" class="pt-5 px-5 pb-5 w-full">
        <div class="flex justify-between items-center">
          <button
            @click="onBack"
            @keydown.enter="onBack"
            class="cursor-pointer border-none bg-transparent p-2 -ml-1"
            v-html="backSvg"
            tabindex="0"
            :aria-label="$t('accessibilityLabels.goBack')"
          ></button>
          <button
            @click="$emit('update:visible', false)"
            @keydown.enter="$emit('update:visible', false)"
            class="cursor-pointer border-none bg-transparent p-2 flex items-center mr-[4px] mt-[4.74px]"
            v-html="cancelSvg"
            tabindex="0"
            :aria-label="$t('accessibilityLabels.closeDialog')"
            :autofocus="visible ? true : false"
          ></button>
        </div>
        <div class="mt-2">
          <h2
            id="create-passphrase-title"
            class="md:!text-[30.72px] !text-[24px] !font-just font-normal !text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2"
          >
            <span v-html="IconPassphrase"></span>
            {{ $t('securityModule.dialog.title') }}
        </h2>
        </div>
      </div>

      <!-- Desktop: previous layout -->
      <div v-else class="flex justify-between items-center p-6 w-full">
        <h2
          id="create-passphrase-title"
          class="md:!text-[30.72px] !text-[24px] !font-just font-normal !text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2"
        >
          <span v-html="IconPassphrase"></span>
          {{ $t('securityModule.dialog.title') }}
      </h2>
        <button
          @click="$emit('update:visible', false)"
          @keydown.enter="$emit('update:visible', false)"
          class="cursor-pointer border-none bg-transparent p-2 flex items-center"
          v-html="cancelSvg"
          tabindex="0"
          :aria-label="$t('accessibilityLabels.closeDialog')"
        ></button>
      </div>
    </template>
    <div class="bg-white px-5 pb-5 rounded-[23.04px] font-just">
      <div class="mb-6">
        <div class="border-[1px] border-solid border-whiteBlue"></div>
      </div>
      <div class="flex flex-col items-start justify-center gap-8">
        <template v-if="step === 1">
          <h2
            class="text-[20px] md:text-[24px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.heading')"
          ></h2>
          <div
            class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.description')"
          ></div>
          <div class="flex flex-col items-start gap-2 text-xs mb-2 w-full">
            <BaseInput
              ref="passphraseInput"
              id="create-passphrase-input"
              v-model="passphrase"
              :label="$t('securityModule.dialog.placeholder')"
              :labelClass="'!text-btnActive/60 !text-[14px]'"
              :labelState="false"
              :rows="4"
              :textarea="true"
              class="mb-2 w-full !border-lightGray"
              auto-grow
              aria-describedby="passphrase-validation"
              aria-required="true"
            />
            <template v-if="passphrase">
              <span class="flex items-center gap-2" id="passphrase-validation" role="status" aria-live="polite">
                <span
                  class="mt-1"
                  v-html="
                    solidCheckIcon({
                      color: isPassphraseLengthValid ? '#00c171' : '#863329',
                    })
                  "

                ></span>
                <span
                  :class="
                    isPassphraseLengthValid ? 'text-greenSuccess' : 'text-alert'
                  "
                  class="text-[16px] font-just font-normal tracking-[0%] leading-[100%]"
                  >{{ $t('securityModule.dialog.msgMinWords') }}</span
                ></span
              >
            </template>
          </div>
        </template>
        <template v-else-if="step === 2">
          <h2
            class="text-[20px] md:text-[24px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.hintStep.title')"
          ></h2>
          <div
            class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
          >
            <span v-html="$t('securityModule.dialog.hintStep.description')"></span>
          </div>

          <BaseInput
            v-model="hint"
            id="passphrase-hint-input"
            class="w-full !border-lightGray"
            :labelClass="'!text-grayOverlay/60 !text-[14px]'"
            :label="$t('securityModule.dialog.hintStep.placeholder')"
            aria-describedby="hint-validation"
          />

          <template v-if="hint">
            <span class="flex items-center gap-2" id="hint-validation" role="status" aria-live="polite" v-if="isHintValid">
              <span
                class="mt-1"
                v-html="
                  solidCheckIcon({
                    color: '#00c171',
                  })
                "
              ></span>
              <span
                :class="'text-greenSuccess'"
                class="text-[16px] font-just font-normal tracking-[0%] leading-[100%]"
                >{{ $t('securityModule.dialog.hintStep.hintValid') }}</span
              ></span
            >
            <span v-else class="flex items-center gap-2" id="hint-validation" role="status" aria-live="polite">
              <span
                class="mt-1"
                v-html="
                  solidXMarkIcon({
                    color: '#863329',
                  })
                "
              ></span>
              <span
                class="text-[16px] font-just font-normal text-alert tracking-[0%] leading-[100%]"
              >
                {{
                  isHintLengthInvalid
                    ? $t('securityModule.dialog.hintStep.hintLengthValid')
                    : $t('securityModule.dialog.hintStep.hintInvalid')
                }}</span
              >
            </span>
          </template>
          <template v-else>
            <span class="flex items-center gap-2" id="hint-validation">
              <span
                class="mt-1"
                v-html="
                  solidCheckIcon({
                    color: '#494949',
                  })
                "
              ></span>
              <span
                class="text-[16px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
                >{{ $t('securityModule.dialog.hintStep.hintValidInfo') }}</span
              ></span
            ></template
          >
        </template>
        <template v-else-if="step === 3">
          <h2
            class="text-[20px] md:text-[24px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.reviewStep.title')"
          ></h2>
          <div
            class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
          >
            <span
              v-html="$t('securityModule.dialog.reviewStep.description')"
            ></span>
          </div>

          <BaseInput
            v-model="passphrase"
            id="passphrase-input"
            :textarea="true"
            :rows="4"
            class="w-full !border-lightGray"
            :label="$t('signUpLabels.password')"
            :labelClass="'!text-grayOverlay/60 !text-[14px]'"
          />
          <BaseInput
            v-model="hint"
            id="passphrase-hint-input"
            readonly
            class="w-full !border-lightGray"
            :label="$t('securityModule.dialog.hintStep.placeholder')"
            :labelClass="'!text-grayOverlay/60 !text-[14px]'"
          />
          <template v-if="encrypting">
            <span class="flex items-center gap-2">
              <span class="animate-spin" v-html="loadingIcon()"></span>
              <span
                class="text-[16px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
                >{{ $t('securityModule.dialog.reviewStep.encrypting') }}</span
              ></span
            ></template
          >
        </template>
      </div>
    </div>
    <template #footer>
      <div v-if="step === 1" class="px-5 pb-5 w-full">
        <BaseButton
          class="dialog-btn dialog-btn--accept w-full !bg-btnActive disabled:!bg-grayOverlay"
          :label="$t('commonLabels.next')"
          :disabled="
            !isPassphraseLengthValid ||
            securityStore.loading
          "
          :loading="securityStore.loading"
          @click="goToHintStep"
        />
      </div>
      <div v-else-if="step === 2" class="flex w-full gap-6 p-5">
        <BaseButton
          class="dialog-btn w-1/2 !border-[1px] !border-btnActive"
          outlined
          @click="step = 1"
          :disabled="securityStore.loading"
        >
          <span class="!text-btnActive">{{ $t('commonLabels.back') }}</span>
        </BaseButton>
        <BaseButton
          class="dialog-btn dialog-btn--accept w-1/2 !bg-btnActive disabled:!bg-grayOverlay"
          :label="$t('commonLabels.next')"
          :disabled="securityStore.loading"
          :loading="securityStore.loading"
          @click="goToReviewStep"
        />
      </div>
      <div v-else-if="step === 3" class="flex w-full gap-6 p-5">
        <BaseButton
          class="dialog-btn w-1/2 !border-[1px] !border-btnActive"
          outlined
          @click="step = 2"
          :disabled="encrypting || securityStore.loading"
        >
          <span class="!text-btnActive">{{ $t('commonLabels.back') }}</span>
        </BaseButton>
        <BaseButton
          class="dialog-btn dialog-btn--accept w-1/2 !bg-btnActive"
          :label="$t('securityModule.dialog.reviewStep.submit')"
          :disabled="
            !passphrase || securityStore.loading || encrypting
          "
          @click="onSubmit"
          :loading="encrypting || securityStore.loading"
        />
      </div>
    </template>
  </BaseDialog>
  <!-- Popup opened when tapping back -->
  <LoseEncryptedDataDialog
    v-if="showLoseEncryptedDataDialog"
    v-model:open="showLoseEncryptedDataDialog"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import BaseDialog from "@/components/Base/BaseDialog.vue";
import BaseInput from "@/components/Base/BaseInput.vue";
import LoseEncryptedDataDialog from "@/views/Security/dialogs/LoseEncryptedDataDialog.vue";
import BaseButton from "@/components/Base/BaseButton.vue";
import {
  closeSvg,
  IconPassphrase,
  loadingIcon,
  solidCheckIcon,
  solidXMarkIcon,
  backArrowSvg
} from "@/utils/commonSvg";
import { useSecurityStore } from "@/stores/securityStore";
import { setUpEncryptionWithPassphrase } from "@/utils/common";
import { useI18n } from "vue-i18n";

// Access the global store instances and component props
const securityStore = useSecurityStore();
const { t } = useI18n();
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
});

// Emits custom events to notify parent about dialog state and asset actions
// - "update:visible": Dialog visibility change
// - "submit": Passphrase successfully created
const emit = defineEmits(["update:visible", "submit"]);

// Responsive design tracking for window dimensions
const windowWidth = ref(window.innerWidth);

// Update window dimensions on resize for responsive behavior
function handleResize() {
  windowWidth.value = window.innerWidth;
}

// Setup and cleanup resize event listener
onMounted(() => {
  window.addEventListener("resize", handleResize);
  // Auto-focus first input when dialog opens
  setTimeout(() => {
    if (passphraseInput.value) {
      passphraseInput.value.$el?.querySelector('textarea')?.focus();
    }
  }, 100);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

// Computed properties for UI elements
const cancelSvg = computed(() => closeSvg({ width: "24px", height: "24px" }));
const backSvg = computed(() =>
  backArrowSvg({ width: "24px", height: "24px", color: "#818181" })
);

// Responsive dialog width (full width on mobile, 50% on desktop)
const dialogWidth = computed(() => {
  return windowWidth.value < 768 ? "100%" : "50%";
});

// Wizard step management (1: Passphrase, 2: Hint, 3: Review)
const step = ref(1);
const passphrase = ref("");
const hint = ref("");
const showLoseEncryptedDataDialog = ref(false);
const encrypting = ref(false);
const passphraseInput = ref(null);

// Calculate word count in passphrase (minimum 8 words required)
const wordCount = computed(
  () => passphrase.value.trim().split(/\s+/).filter(Boolean).length
);

// Clean passphrase by converting to lowercase, removing punctuation, and normalizing spaces
const cleanedPassphrase = computed(
  () =>
    passphrase.value
      .toLowerCase()
      .replace(/[^\sa-z]/g, "") // remove punctuation
      .trim()
      .replace(/\s+/g, " ") // normalize spaces
);

// Clean hint text similarly to passphrase for consistency
const cleanedHint = computed(() =>
  hint.value
    .toLowerCase()
    .replace(/[^\sa-z]/g, "")
    .trim()
    .replace(/\s+/g, " ")
);

// Validation: Check if passphrase meets minimum length requirement (8 words)
const isPassphraseLengthValid = computed(() => wordCount.value >= 8);

// Validation: Check for invalid characters in passphrase (only letters and spaces allowed)
const hasInvalidCharacters = computed(() => /[^a-z\s]/i.test(passphrase.value));

// Validation: Check if passphrase is strong (only letters, no numbers/special chars, valid words)
const isPassphraseStrong = computed(() => {
  const words = cleanedPassphrase.value.split(" ").filter(Boolean);
  return (
    !hasInvalidCharacters.value && words.every((word) => /^[a-z]+$/.test(word))
  );
});

// Validation: Check if hint is too short (minimum 8 characters)
const isHintLengthInvalid = computed(
  () => !hint.value || hint.value.length < 8
);

const isPassphraseLengthInvalid = computed(
  () => !passphrase.value || passphrase.value.length < 8
);

// Extract meaningful words from passphrase (words longer than 2 characters)
const cleanedPassphraseWords = computed(() =>
  cleanedPassphrase.value.split(" ").filter((word) => word.length > 2)
);

// Common words to ignore when checking hint security
const stopWords = ["and", "the", "of", "to", "in", "on", "at", "a", "an"];

// Validation: Check if hint contains any meaningful words from the passphrase
const isHintInvalid = computed(() => {
  const hintText = cleanedHint.value;
  // Filter out stop words from passphrase
  const meaningfulWords = cleanedPassphraseWords.value.filter(
    (word) => !stopWords.includes(word)
  );

  // Check if any meaningful word from passphrase appears in the hint
  return meaningfulWords.some((word) => {
    const regex = new RegExp(`\\b${word}\\b`, "i"); // match full word
    return regex.test(hintText);
  });
});

// Overall hint validation (must be valid length and not contain passphrase words)
const isHintValid = computed(
  () => !isHintLengthInvalid.value && !isHintInvalid.value
);

//  Navigate to the Hint setup step (Step 2)
function goToHintStep() {
  step.value = 2;
}

//  Navigate to the Review step (Step 3)
function goToReviewStep() {
  step.value = 3;
}

// Handle back button on mobile: open LoseEncryptedDataDialog popup
function onBack() {
  showLoseEncryptedDataDialog.value = true;
}

/**
 * Handle form submission
 * 1. Saves passphrase and hint to security store
 * 2. Sets up encryption with the provided passphrase
 * 3. Emits 'submit' event with the passphrase and hint
 */
async function onSubmit() {
  encrypting.value = true;
  try {
    // Store passphrase and hint in security store
    securityStore.passphrase = passphrase.value;
    securityStore.hint = hint.value;

    // Set up encryption with the provided passphrase
    securityStore.encryptionInfo = await setUpEncryptionWithPassphrase(
      passphrase.value
    );

    // If encryption setup is successful, emit submit event after a short delay
    if (securityStore.encryptionInfo) {
      setTimeout(() => {
        encrypting.value = false;
        emit("submit", { passphrase: passphrase.value, hint: hint.value });
      }, 2500);
    }
  } catch (error) {
        console.error("Error fetching encryption status:", error);
    throw error;
  } finally {
    setTimeout(() => {
      encrypting.value = false;
    }, 2500);
  }
}

// Watch for dialog visibility changes to reset form state
watch(
  () => props.visible,
  (val) => {
    if (!val) {
      step.value = 1;
      passphrase.value = "";
      hint.value = "";
    }
  }
);
</script>

<style>
.mobile-dialog {
  height: 100% !important;
  max-height: 100% !important;
}
</style>
